/**
 * Ticket 6 — Outreach Library (BDR / AE read-only surface)
 *
 * Mount inside pages/dashboards/BdrView.tsx and pages/dashboards/AeView.tsx
 * (or wherever the role's main dashboard renders). Shows only approved
 * hooks. Server enforces approved-only for non-marketing roles, so even if
 * the user opens devtools and rewrites the query string to status=draft,
 * the server still returns approved-only.
 */

import { useEffect, useState } from "react";
import { api } from "@/lib/session";
import { useToast } from "@/hooks/use-toast";

interface OutreachHook {
  id: string;
  marketing_event_id: string | null;
  hook_type: "invite" | "participation" | "round_table";
  approved_text: string | null;
  draft_text: string;
}

const TYPE_LABEL = {
  invite: "Invite",
  participation: "Participation",
  round_table: "Round Table",
} as const;

export function OutreachLibrary({ eventId }: { eventId?: string }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [hooks, setHooks] = useState<OutreachHook[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (eventId) params.set("event_id", eventId);
        const j = await api<{ hooks?: OutreachHook[] }>(`/api/marketing/hooks?${params.toString()}`);
        if (!cancelled) setHooks(j?.hooks ?? []);
      } catch (e: any) {
        if (!cancelled) {
          const detail = e instanceof Error && e.message ? ` · ${e.message}` : "";
          toast({ title: "Could not load outreach library", description: `Please refresh${detail}`, variant: "destructive" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, toast]);

  if (loading) {
    return <div data-testid="outreach-library-loading" style={{ padding: 16, color: "#525258" }}>Loading approved hooks…</div>;
  }
  if (hooks.length === 0) {
    return (
      <div data-testid="outreach-library-empty" style={{ padding: 16, color: "#525258" }}>
        No approved outreach hooks yet. Marketing must approve drafts before they appear here.
      </div>
    );
  }

  function copyHook(text: string) {
    navigator.clipboard.writeText(text).then(
      () => toast({ title: "Hook copied" }),
      () => toast({ title: "Copy failed", variant: "destructive" }),
    );
  }

  return (
    <div data-testid="outreach-library">
      <h2 style={{ fontSize: 14, fontWeight: 700, color: "#0B0B0F", margin: "0 0 12px" }}>Outreach library</h2>
      <div style={{ display: "grid", gap: 12 }}>
        {hooks.map((h) => {
          const text = h.approved_text ?? h.draft_text;
          return (
            <div
              key={h.id}
              data-testid={`library-hook-${h.id}`}
              style={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: 8, padding: 12 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#525258" }}>{TYPE_LABEL[h.hook_type]}</span>
                <button
                  type="button"
                  onClick={() => copyHook(text)}
                  data-testid={`library-copy-${h.id}`}
                  style={{ padding: "4px 10px", fontSize: 11, fontWeight: 600, borderRadius: 6, background: "transparent", color: "#0B0B0F", border: "1px solid rgba(15,23,42,0.15)", cursor: "pointer" }}
                >Copy</button>
              </div>
              <p style={{ fontSize: 12, color: "#0B0B0F", margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
